import React, { Fragment, useState } from 'react'
import { Link, useStaticQuery, graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { Dialog, Popover, Transition } from '@headlessui/react'
import { MenuIcon, XIcon } from '@heroicons/react/outline'
import { filterProducts } from '../utils/Utils'

const navigation = {
  pages: [{ name: 'Kontakt', href: '/kontakt' }],
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function Nav() {
  const [open, setOpen] = useState(false)

  const data = useStaticQuery(graphql`
    query NavQuery {
      allWpCategory(filter: { name: { ne: "Uncategorized" } }) {
        nodes {
          name
          slug
          rwProducts {
            nodes {
              slug
              id
              title
              rwItemInformation {
                oeffentlichGelistet
              }
            }
          }
        }
      }
      wpPartnerSeite {
        title
        partnerseite {
          disableProducts {
            ... on WpRwProduct {
              id
            }
          }
          partnerHomepageUrl
          kontakt {
            tel
            telLink
          }
          identity {
            invertDefaultMenu
            invertiertesLogoOptional {
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(
                    height: 96
                    placeholder: BLURRED
                    formats: [AUTO, WEBP, AVIF]
                  )
                }
              }
              altText
            }
            navigationDesktop {
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(
                    height: 96
                    placeholder: BLURRED
                    formats: [AUTO, WEBP, AVIF]
                  )
                }
              }
              altText
            }
          }
        }
      }
    }
  `)

  const partnerIdentity = data.wpPartnerSeite?.partnerseite?.identity
  const productIdsToFilter = data.wpPartnerSeite.partnerseite.disableProducts

  const darkMode = partnerIdentity?.invertDefaultMenu

  return (
    <div className={classNames(darkMode && 'dark')}>
      {/* Mobile menu */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 left-auto z-40 flex lg:hidden"
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <div className="relative flex flex-col w-full max-w-xs pb-12 overflow-y-auto font-medium text-white bg-black font-barlowCondensed">
              <div className="flex justify-end px-4 pt-5 mb-4">
                <button
                  type="button"
                  className="inline-flex items-center justify-center p-2 -m-2 text-gray-400 rounded-md"
                  onClick={() => setOpen(false)}
                >
                  <span className="mr-2">Menü schließen</span>
                  <XIcon className="w-6 h-6" aria-hidden="true" />
                </button>
              </div>

              <div className="space-y-3">
                <a
                  className='block px-4 py-3'
                  href={
                    data.wpPartnerSeite?.partnerseite?.partnerHomepageUrl ?? '/'
                  }
                  title={data.wpPartnerSeite?.partnerseite?.partnerHomepageUrl}
                >
                  Zurück zur Homepage
                </a>
              </div>

              <div className="space-y-3">
                <Link className="block px-4 py-3" to="/">
                  Alle Produkte
                </Link>
              </div>

              {data.allWpCategory.nodes.map(category => {
                const categoryProducts = filterProducts(category.rwProducts.nodes, productIdsToFilter)
                if (categoryProducts.length < 1) return
                return (
                  <div key={category.name} className="ml-2 space-y-2">
                    <Link
                      to={`/kategorien/${category.slug}`}
                      className="flex items-center px-4 py-3 text-sm font-medium text-gray-200 hover:text-gray-100"
                    >
                      {category.name}
                    </Link>
                  </div>
                )}
              )}

              <div className="mt-2 space-y-3 border-t border-gray-200">
                <div className="flow-root">
                  {navigation.pages.map(page => (
                    <Link
                      key={page.name}
                      to={page.href}
                      className="flex items-center px-4 py-3 text-sm font-medium text-gray-200 hover:text-gray-100"
                    >
                      {page.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </Transition.Child>
        </Dialog>
      </Transition.Root>

      <header className="relative bg-white dark:bg-[color:var(--primary-colour)]">
        <div className="flex items-center justify-between h-10 gap-2 px-4 text-sm font-medium text-white sm:px-6 lg:px-8 bg-[color:var(--primary-colour)]">
          <a
            className='hidden sm:block'
            href={
              data.wpPartnerSeite?.partnerseite?.partnerHomepageUrl ?? '/'
            }
            title={data.wpPartnerSeite?.partnerseite?.partnerHomepageUrl}
          >
            Zurück zur Homepage
          </a>
          <p>
            Support-Hotline:{' '}
            <a href="tel:+49303229521558">+49 (0)30 322 9521 558</a>
          </p>
        </div>

        <nav
          aria-label="Top"
          className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8"
        >
          <div>
            <div className="flex items-center justify-between h-28">
              <button
                type="button"
                className="flex order-2 p-2 space-x-2 text-gray-900 dark:text-gray-100 bg-white dark:bg-[color:var(--primary-colour)] rounded-md lg:hidden"
                onClick={() => setOpen(true)}
              >
                <span className="sr-only">Menü öffnen</span>
                <span className="font-medium uppercase">Menü</span>
                <MenuIcon className="w-6 h-6" aria-hidden="true" />
              </button>

              {/* Logo */}
              <div className="flex mr-auto lg:mr-0">
                <a
                  href={
                    data.wpPartnerSeite?.partnerseite?.partnerHomepageUrl ?? '/'
                  }
                  title={data.wpPartnerSeite?.partnerseite?.partnerHomepageUrl}
                >
                  <span className="sr-only">Partner</span>
                  {!darkMode &&
                    (partnerIdentity?.navigationDesktop?.localFile
                      ?.extension !== 'svg' ? (
                      <GatsbyImage
                        className="w-auto max-w-[11rem]"
                        image={
                          partnerIdentity?.navigationDesktop?.localFile
                            ?.childImageSharp?.gatsbyImageData
                        }
                        alt="logo"
                      />
                    ) : (
                      <img
                        className="w-auto max-w-[11rem] min-h-[5rem]"
                        src={
                          partnerIdentity?.navigationDesktop?.localFile
                            ?.publicURL
                        }
                        alt="logo"
                      />
                    ))}
                  {darkMode &&
                    (partnerIdentity?.invertiertesLogoOptional?.localFile
                      ?.extension !== 'svg' ? (
                      <GatsbyImage
                        className="w-auto max-w-[11rem]"
                        image={
                          partnerIdentity?.invertiertesLogoOptional?.localFile
                            ?.childImageSharp?.gatsbyImageData
                        }
                        alt="logo"
                      />
                    ) : (
                      <img
                        className="w-auto max-w-[11rem] min-h-[5rem]"
                        src={
                          partnerIdentity?.invertiertesLogoOptional?.localFile
                            ?.publicURL
                        }
                        alt="logo"
                      />
                    ))}
                </a>
              </div>

              {/* Flyout menus */}
              <Popover.Group className="hidden lg:ml-8 lg:block lg:self-stretch">
                <div className="flex h-full space-x-8">
                  <Popover className="flex">
                    {({ open }) => (
                      <>
                        <div className="relative flex">
                          <Popover.Button
                            className={classNames(
                              open
                                ? 'border-[color:var(--primary-colour)] text-[color:var(--primary-colour)] dark:text-white'
                                : 'border-transparent text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200',
                              'relative z-10 flex items-center transition-colors ease-out duration-200 uppercase text-lg border-b-2 -mb-px pt-px'
                            )}
                          >
                            Produkte
                          </Popover.Button>
                        </div>

                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-200"
                          enterFrom="opacity-0"
                          enterTo="opacity-100"
                          leave="transition ease-in duration-150"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Popover.Panel className="absolute inset-x-0 z-10 text-sm text-gray-500 top-full">
                            {/* Presentational element used to render the bottom shadow, if we put the shadow on the actual panel it pokes out the top, so we use this shorter element to hide the top of the shadow */}
                            <div
                              className="absolute inset-0 bg-white shadow top-1/2"
                              aria-hidden="true"
                            />

                            <div className="relative bg-white">
                              <div className="px-8 mx-auto max-w-7xl">
                                <div className="grid grid-cols-1 py-16 gap-y-10 gap-x-8">
                                  <div className="grid grid-cols-4 row-start-1 text-sm gap-y-10 gap-x-8">
                                    {data.allWpCategory.nodes.map(category => {
                                      const categoryProducts = filterProducts(category.rwProducts.nodes, productIdsToFilter)
                                      if (categoryProducts.length < 1) return
                                      return (
                                      <div key={category.name}>
                                        <Link
                                          to={`/kategorien/${category.slug}`}
                                          id={`${category.name}-heading`}
                                          className="font-medium text-gray-900 hover:underline"
                                        >
                                          {category.name}
                                        </Link>
                                        <ul
                                          aria-labelledby={`${category.name}-heading`}
                                          className="mt-6 space-y-6 sm:mt-4 sm:space-y-4"
                                        >
                                          {categoryProducts.map(
                                            item => {
                                              if (
                                                !item.rwItemInformation
                                                  .oeffentlichGelistet
                                              ) {
                                                return
                                              }
                                              return (
                                                <li
                                                  key={item.title}
                                                  className="flex"
                                                >
                                                  <Link
                                                    to={`/produkte/${item.slug}`}
                                                    className="hover:text-gray-800"
                                                  >
                                                    {item.title}
                                                  </Link>
                                                </li>
                                              )
                                            }
                                          )}
                                        </ul>
                                      </div>
                                    )})}
                                  </div>
                                  <Link
                                    to="/produkte"
                                    title="alle Produkte ansehen"
                                    className="ml-auto"
                                  >
                                    Alle Produkte {'->'}
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </Popover.Panel>
                        </Transition>
                      </>
                    )}
                  </Popover>
                  <Link
                    to="/kategorien"
                    title="Alle Kategorien"
                    className="flex items-center text-lg text-gray-700 uppercase dark:text-gray-300 nav-item"
                    activeClassName="nav-item--active"
                  >
                    Kategorien
                  </Link>

                  {navigation.pages.map(page => (
                    <Link
                      key={page.name}
                      to={page.href}
                      className="flex items-center text-lg text-gray-700 uppercase dark:text-gray-300 nav-item"
                      activeClassName="nav-item--active"
                    >
                      {page.name}
                    </Link>
                  ))}
                </div>
              </Popover.Group>
            </div>
          </div>
        </nav>
      </header>
    </div>
  )
}
